import { HStack, Icon, Skeleton, Stack, Text } from '@chakra-ui/react';
import { BriefcaseIcon } from '@heroicons/react/outline';
import { useTranslations } from 'use-intl';

import { useGetSubscriptionQuery } from '@blockpulse3/graphql/hooks';
import { formatReferenceLabel } from '@blockpulse3/helpers';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';

type HoldingMethodTypeMessage = keyof Messages['HoldingMethodTypeValue'];

type Props = {
  subscriptionId: string;
};

/**
 * SubscriptionDetailsHoldingMethod.
 * Holding method logic section of the susbcription side panel.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriptionDetailsHoldingMethod({ subscriptionId }: Props): JSX.Element {
  const i18nHoldingMethodTypeValue = useTranslations('HoldingMethodTypeValue');
  const i18nSubscriptionDetailsHoldingMethod = useTranslations('SubscriptionDetailsHoldingMethod');

  const { data, loading, error } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });

  if (error) {
    return <ErrorQueryCard h="60px" mx="3" />;
  }

  const subscription = data?.subscription;

  /* ** Fiscal account name ** */
  const fiscalAccount = subscription?.fiscalAccount;
  const { holdingMethod } = subscription || {
    name: i18nSubscriptionDetailsHoldingMethod('DefaultHoldingMethod'),
  };

  const referenceLabel = subscription?.reference
    ? formatReferenceLabel(subscription?.identity.name, subscription?.reference.label)
    : '';

  const fiscalAccountTitle = fiscalAccount
    ? fiscalAccount.name + (fiscalAccount?.bankCode ? ` (${fiscalAccount.bankCode})` : '')
    : '';
  const holdingMethodTitle = i18nHoldingMethodTypeValue(holdingMethod as HoldingMethodTypeMessage);

  return (
    <Stack px="4" spacing="3">
      <Text color="gray.600" fontWeight="600">
        {i18nSubscriptionDetailsHoldingMethod('HoldingMethod')}
      </Text>
      {loading ? (
        <Skeleton h="60px" />
      ) : (
        <>
          <HStack>
            <Icon
              as={BriefcaseIcon}
              backgroundColor="gray.50"
              borderRadius="100px"
              boxSize="30px"
              color="gray.500"
              p="4px"
            />
            <Text fontSize="sm" fontWeight="600">
              {fiscalAccountTitle || holdingMethodTitle}
            </Text>
          </HStack>
          {fiscalAccount && (
            <>
              <HStack justify="space-between">
                <Text color="gray.500" fontSize="sm" fontWeight="500">
                  {i18nSubscriptionDetailsHoldingMethod('AccountNumber')}
                </Text>
                <Text fontSize="sm" fontWeight="500">
                  {fiscalAccount.accountNumber}
                </Text>
              </HStack>
              {fiscalAccount?.bankEmail && (
                <HStack justify="space-between">
                  <Text color="gray.500" fontSize="sm" fontWeight="500">
                    {i18nSubscriptionDetailsHoldingMethod('BankEmail')}
                  </Text>
                  <Text fontSize="sm" fontWeight="500">
                    {fiscalAccount.bankEmail}
                  </Text>
                </HStack>
              )}
            </>
          )}
          {referenceLabel && (
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="sm" fontWeight="500">
                {i18nSubscriptionDetailsHoldingMethod('ReferenceLabel')}
              </Text>
              <Text fontSize="sm" fontWeight="500">
                {referenceLabel}
              </Text>
            </HStack>
          )}
        </>
      )}
    </Stack>
  );
}
